// "Kes ve sil" — cut a clip at the playhead, throw away the piece before it
// and pull what is left back to where the clip started, so the clip keeps its
// slot on the row and no gap opens in its place.
//
// Kept pure and separate from index.jsx so the frame/offset math can be
// tested: the three media shapes each express their in-point differently
// (scene-audio in seconds via audioOffset, audio boxes in frames via
// trimStart, videos in frames plus a decoupled play window).

/**
 * @param {object} layer   timeline layer (see buildLayers.js)
 * @param {number} cutFrame  playhead position
 * @param {number} fps
 * @returns {null|{startFrame, endFrame, extra, layerPatch}} null when the cut
 *   does not land inside the clip. `extra` is what the host persists,
 *   `layerPatch` what the optimistic in-memory layer needs.
 */
export function resolveHeadTrim({ layer, cutFrame, fps }) {
  if (!layer || typeof cutFrame !== "number") return null;
  // Same in-clip guard the split uses — at least a frame has to survive on
  // either side of the cut.
  if (cutFrame <= layer.startFrame + 1 || cutFrame >= layer.endFrame - 1) return null;

  const removed = cutFrame - layer.startFrame;
  const startFrame = layer.startFrame; // snap back to the clip's own start
  const endFrame = layer.endFrame - removed;

  if (layer.type === "scene-audio") {
    const audioOffset = (parseFloat(layer.audioData?.audioOffset) || 0) + removed / fps;
    return {
      startFrame,
      endFrame,
      extra: { audioOffset },
      layerPatch: { audioData: { ...(layer.audioData || {}), audioOffset } },
    };
  }

  // Only source that was actually PLAYING before the cut is lost. For audio
  // the play window is the clip itself, so that is the whole removed head;
  // for a video whose play window starts later it is less, or none at all.
  const playStart =
    typeof layer.playStartFrame === "number" ? layer.playStartFrame : layer.startFrame;
  const trimStart = (layer.trimStart ?? 0) + Math.max(0, cutFrame - playStart);
  const extra = { trimStart };
  if (typeof layer.playStartFrame === "number") {
    // The play window rides along with the clip, same as a clip drag does.
    extra.playStartFrame = Math.max(startFrame, layer.playStartFrame - removed);
  }
  return { startFrame, endFrame, extra, layerPatch: { ...extra } };
}
